// Re:Vault app entry. Wires the shell, boots the proxy, and applies settings live.
import "./inapp-gate.js"; // first, so an in-app browser hears why nothing works even if the rest cannot start
import { announce } from "./announce.js";
import { keepFocusVisible } from "../shared/keep-focus-visible.js";
import { applyTheme } from "./theme.js";
import { FONTS, loadFont, loadDisplayFont } from "../shared/fonts.js";
import { initWallpaper } from "./wallpaper.js";
import { initCloak, applyCloak, setTitleCount } from "./cloak.js";
import { initSlots } from "./slots-gate.js";
import { isPerf } from "./perf.js";
import { boot, setEngine, setTransport, status, reconnect, connectionTitle, decodeOpen, remote } from "./proxy.js";
import { render, frames, remoteNav, remoteIcon, onRender, favOk } from "./view.js";
import { navigate, navigateTab, back, forward, reload } from "./navigation.js";
import { newTab, openSettings, openInternal, closeTab, selectTab, pinTab, closeOthers, closeRight, moveTab } from "./tabs.js";
import { openLinux, linuxBusy, anyUnsaved } from "./linux-open.js";
import { newTabAction, reopenClosed, duplicateActive, openHome } from "./session.js";
import { initFolderSearch, openBook, openFolder } from "./folders.js";
import { isGamePath, playPass, keepPlayPass } from "./play-pass.js";
import { canIsolate } from "../shared/isolation.js";
import { initStart, setHallBadge, continueLinks } from "./start.js";
import { initFavoritesUI } from "./favorites-ui.js";
import { initShortcuts } from "./shortcuts.js";
import { initBell, bellNext } from "./bell.js";
import { initMusic, focusPlay, focusDim, focusStop, focusOn, musicCtl } from "./music.js";
import { initCollapse, applyCollapse } from "./collapse.js";
import { restoreSession, restoreOffer } from "./session-store.js";
import { initRestoreOffer } from "./restore-offer.js";
import { attachAutofill } from "./autofill.js";
import { tickClock } from "./clock.js";
import { initGlobalErrorHandlers, remoteError, retry, reloadWithEngine, remoteFail, connState, loadRecovery, countStranded } from "./errors.js";
import { initIsland, emitIsland, focusIsland, islandOn, islandAddr } from "./island-gate.js";
import { commands, loadSettingsIndex } from "./commands.js";
import { toggleFavorite } from "./favorites-ui.js";
import * as favorites from "./favorites.js";
import { zoomActive, openFind } from "./zoom.js";
import { builtIns, openShortcut, openAll, ICONS } from "./all-shortcuts.js";
import { dispTitle } from "./obf.js";
import { initUpdates } from "./update.js";
import { initExtensions } from "./extensions.js";
import { initExtFrames } from "./ext-frames.js";
import { initExtUI } from "./ext-ui.js";
import { initNavMore } from "./nav-more.js";
import { initStatus, statusState, statusName, statusGlyph, openStatus } from "./status.js";
import { initExtBar, extBarApi } from "./ext-bar.js";
import { paintStudying } from "../shared/studying-now.js";
import { initPresence, onOnline, lastOnline, lastHall, lastStudying, lastGarden, setFocusing, carryOn } from "./presence.js";
import { paintGarden } from "../shared/commons.js";
import { initHelperDock } from "./helper-dock.js";
import { checkOnboarding, initOnboarding, welcomeBehind } from "./onboarding-gate.js";
import { initHome } from "./home-gate.js";
import { initBumper } from "./bumper-gate.js";
import { inOS, initOSHost, openDesktop } from "./os-host.js";
import { openShareSheet } from "../shared/share-gate.js";
import { takeArrival } from "./arrival.js";
import { initWhatsNew, openWhatsNew } from "./whats-new.js";
import { initInstall } from "./install.js";
import { setQuestToast, checkMilestones, track, questStats, onQuestChange } from "./quests.js";
import * as activity from "../shared/activity.js";
import { markVisit, TOTALS_KEY } from "../shared/activity.js";
import { readJSON, writeJSON, subscribe as storeSubscribe } from "../shared/store.js";
import { isDevice } from "../shared/save-keys.js";
import { loadHealth } from "../shared/health-gate.js";
import { consumeRedirectToken, consumeAuthReturn, refreshProfile, getSession, setSession, initTopBridge, onAccountChange, syncUp, syncDown, fetchBackup, signIn, setSignInSheet, accountConfig, authedFetch, originTicket, startRedirect, keepSessionFresh, getStanding } from "./account.js";
import { state, active } from "./state.js";
import { addrInput, formEl, backBtn, fwdBtn, reloadBtn, menuBtn, newTabBtn, loaderSkip, startEl, setAddr, $ } from "./dom.js";
import { maxObf } from "./obf.js";
import { applyObfuscation } from "../shared/obfuscate.js";
import { subscribe, get, set, setMany } from "../shared/settings-store.js";
import { openableUrl } from "../shared/open-url.js";
import { TOOLS } from "../shared/tools-manifest.js";
import { WALLPAPER_PRESET_MAP, SHORTCUTS, MUSIC_PRESET_MAP } from "../shared/settings-schema.js";

const PAGE_TITLES = { "/whats-new": "What's new", "/supporter": "Supporter", "/": "New Tab", "/about": "About", "/privacy": "Privacy", "/terms": "Terms", "/settings": "Settings", "/help": "Help", "/status": "Status", "/tools": "Tools", "/linux": "Linux", "/hall": "Study Hall", "/schools": "For schools", "/accessibility": "Accessibility" };

// Is this a first visit? Read before anything below writes to storage (onboarding-gate.js). The
// start page bumper's check comes first, so it sees what the pre-paint saw: checkOnboarding() may
// mark a long-unanswered welcome dismissed.
const welcomeDone = welcomeBehind();
const onboarding = checkOnboarding();

// --- paint chrome immediately --------------------------------------------
applyTheme();
// Capture a session token returned in the URL fragment after a Google sign-in redirect,
// then keep the profile fresh for this session.
initTopBridge(); // relay sign-in navigation from the sandboxed settings iframe
// Quest completions (and the home card) surface a brief toast.
let qt = null, qtTimer = 0;
const toast = (msg) => {
	if (islandOn()) { announce(msg); emitIsland("note", { text: msg, at: Date.now() }); return; } // shown in the island for a moment
	if (!qt) { qt = document.createElement("div"); qt.className = "rv-quest-toast"; qt.setAttribute("aria-hidden", "true"); document.body.appendChild(qt); }
	qt.textContent = msg; qt.hidden = false; qt.classList.add("show");
	announce(msg);
	clearTimeout(qtTimer); qtTimer = setTimeout(() => { qt.classList.remove("show"); }, 3200);
};
setQuestToast(toast);
// A save the browser refused (out of space): say so, at most once a minute. Background notes about
// this device (ids, counters, timers) failing is not the student's work, so those stay quiet.
let saveFailAt = 0;
window.addEventListener("rv:save-failed", (e) => {
	if (Date.now() - saveFailAt < 60000 || (e.detail && isDevice(String(e.detail.key)))) return;
	saveFailAt = Date.now();
	toast("Not saved: this browser is out of space for Re:Vault. Remove a picture wallpaper or download a save file.");
});
keepFocusVisible();
consumeRedirectToken();
consumeAuthReturn(); // a sign-in started in Re:Vault OS goes back to the desktop
// The sign-in sheet (src/client/signin/): a lazy chunk, loaded the first time sign-in opens. It
// stays in this top window, where Discord and Ep3 flows wait while the student is in another tab.
let sheet = null;
function openSignIn(opts) {
	if (!sheet) {
		const css = document.createElement("link");
		css.rel = "stylesheet"; css.href = __RVM.signinCss;
		document.head.appendChild(css);
		sheet = import(__RVM.signinJs).catch((e) => { sheet = null; throw e; }).then((m) => m.start({
			config: accountConfig, authedFetch, originTicket, startRedirect, setSession, refreshProfile,
			signedIn: () => !!getSession(),
			sandboxActive: () => !!remote(),
			openProxied: (url) => { const id = newTab(); navigateTab(id, url); return id; },
			closeTab: (id) => { if (state.tabs.some((t) => t.id === id)) closeTab(id); },
			toast,
			// Settings (another document) re-reads its list on this storage event.
			changed: () => { try { localStorage.setItem("revault:signin-changed", String(Date.now())); } catch (e) { void e; } },
		}));
	}
	sheet.then((s) => s.open(opts || {}), () => toast("Sign-in couldn't load. Check your connection and try again."));
}
setSignInSheet(openSignIn);
// "#revault-signin": a page outside the app asked for the sheet. "#revault-linked=<method>": back
// from adding a way to sign in on the auth origin.
{
	const hm = /^#revault-(signin$|linked=([a-z]{3,12})$)/.exec(location.hash);
	if (hm) {
		try { history.replaceState(null, "", location.pathname + location.search); } catch (e) { void e; }
		if (!hm[2]) setTimeout(() => { if (!getSession()) openSignIn(); }, 0);
		else if (getSession()) setTimeout(() => toast("Added. You can sign in that way now."), 400);
	}
}
if (getSession()) refreshProfile();
keepSessionFresh();
// The supporter state sync is a lazy chunk (app/supporter-sync.js): a few seconds after load, so
// the start screen never waits for it. A `let`, so esbuild leaves the import alone.
let supporterSyncUrl = __RVM.supporterSyncJs; // eslint-disable-line prefer-const
setTimeout(() => { import(supporterSyncUrl).then((m) => m.initSupporterSync({ getSession, authedFetch, onAccountChange })).catch(() => {}); }, 4000);
tickClock(); // the clock is the LCP element - paint it before the rest of init runs
initWallpaper();
initCloak();
initGlobalErrorHandlers();
applyObfuscation(maxObf());

// --- wire toolbar + dashboard --------------------------------------------
newTabBtn.addEventListener("click", newTabAction);
backBtn.addEventListener("click", back);
fwdBtn.addEventListener("click", forward);
reloadBtn.addEventListener("click", reload);
menuBtn.addEventListener("click", openSettings);
{ const sb = $("rv-share"); if (sb) sb.addEventListener("click", () => openShareSheet()); }
// Account button in the toolbar: opens Settings (Account is the top section) and shows
// the signed-in initial as a small avatar.
{
	const acctBtn = document.getElementById("rv-account-btn");
	const av = document.getElementById("rv-account-av-mini");
	if (acctBtn) {
		acctBtn.addEventListener("click", () => { if (getSession()) openSettings(); else openSignIn(); });
		const svg = acctBtn.querySelector("svg");
		const paint = (sess) => {
			// Signed in means a session token, not a name: a Google account with no name claim has
			// an empty profile name (the email is never stored), and it is still signed in.
			const pr = (sess && sess.profile) || {};
			if (sess && sess.token) {
				// Explicit inline display beats the [hidden] attribute + CSS cascade reliably.
				acctBtn.classList.add("signed");
				if (av) { av.hidden = false; av.style.display = "flex"; av.textContent = (pr.name || "?").charAt(0).toUpperCase(); av.style.background = "var(--accent)"; }
				if (svg) svg.style.display = "none";
				acctBtn.title = pr.name ? "Signed in as " + pr.name : "Account";
			} else {
				acctBtn.classList.remove("signed");
				if (av) av.style.display = "none";
				if (svg) svg.style.display = "";
				acctBtn.title = "Sign in";
			}
		};
		onAccountChange(paint);
		paint(getSession());
	}
}
// After Enter in the address bar the page takes focus once it loads, like Chrome's own address bar.
formEl.addEventListener("submit", (e) => { e.preventDefault(); addrInput.dataset.sent = "1"; navigate(addrInput.value); });
addrInput.addEventListener("input", () => { delete addrInput.dataset.sent; state.editing = true; const a = active(); if (a) a.addr = addrInput.value; });
attachAutofill(addrInput, (v) => { addrInput.dataset.sent = "1"; navigate(v); });
// Under max obfuscation the address bar shows a scrambled string; reveal the
// real URL on focus (so it's editable) and re-scramble on blur.
addrInput.addEventListener("focus", () => { delete addrInput.dataset.sent; if (maxObf()) addrInput.value = addrInput.dataset.real || addrInput.value; });
addrInput.addEventListener("blur", () => { if (maxObf() && !state.editing) { const a = active(); setAddr(a ? a.addr : ""); } });
initFolderSearch();
initStart();
// Study Hall's unread badge, only in a browser that has used Study Hall and is signed in, so a fresh
// profile never even loads it. In Island Mode the island shows the mention, so the toast is only spoken.
try {
	if (localStorage.getItem("revault:hall:used") && getSession()) {
		import(__RVM.hallBadgeJs).then((m) => m.initHallBadge({ getSession, toast: (t) => (islandOn() ? announce(t) : toast(t)), announce, setBadge: (b) => { setHallBadge(b); emitIsland("hall", b); }, setTitleCount, hallInFront: () => { const a = active(); return !!(a && a.internal === "/hall"); } })).catch(() => {});
	}
} catch { /* storage blocked */ }
initFavoritesUI();
initShortcuts();
initBell();
initMusic();
initCollapse();
if (loaderSkip) loaderSkip.addEventListener("click", () => { const a = active(); if (a) { a.skipLoader = true; render(); } });

// Internal pages opened inside the BiB ask the app to open links as tabs. Only a
// fixed set of first-party routes may be opened, with a trusted title - so a
// crafted message (incl. from same-origin proxied content) can't load an
// arbitrary internal/engine-prefixed path or spoof a tab title. (A 404 page can
// legitimately ride inside a proxied tab, so we intentionally don't gate on the
// sender frame - proxied content is already same-origin and gains nothing here.)
let focusTab = null, focusWatch = 0;
const tabOf = (win) => { for (const [id, fr] of frames) if (fr.contentWindow === win) return id; return null; };
const OPENABLE = new Set(["/whats-new", "/supporter", "/", "/settings", "/about", "/privacy", "/terms", "/help", "/tools", "/status", "/linux", "/hall", "/schools", "/accessibility"]);
// /privacy, optionally at one of its paragraphs ("/privacy#no-dms").
const privacyUrl = (u) => (typeof u === "string" && /^\/privacy(?:#[a-z-]{1,24})?$/.test(u) ? u : "");
window.addEventListener("message", (e) => {
	if (e.origin !== location.origin) return;
	const d = e.data;
	if (!d) return;
	// An internal BiB page (e.g. /status) asking to close its own tab and return to
	// the app - matched by which frame's window sent the message, so a page can only
	// ever close the tab it lives in. This is what makes /status's "Back to Re:Vault"
	// return to the app instead of loading a second Re:Vault nested in the iframe.
	if (d.type === "revault-close") {
		for (const [id, fr] of frames) { if (fr.contentWindow === e.source) { closeTab(id); break; } }
		return;
	}
	// A link clicked in Study Hall opens in a new PROXIED tab (DESIGN.md 16.5), never a raw
	// _blank window. Only the Study Hall frame may ask (matched by the sending window), and only
	// for an http(s) URL of sane length without control characters.
	if (d.type === "revault-open-url") {
		let src = null;
		for (const [id, fr] of frames) { if (fr.contentWindow === e.source) { src = state.tabs.find((t) => t.id === id); break; } }
		if (!src || src.internal !== "/hall") return;
		const url = openableUrl(d.url);
		if (!url) return;
		navigateTab(newTab(), url); // proxied, like openProxied in folders.js
		return;
	}
	// The timer tool, or Study Hall's "Room sound", asks for a focus soundscape (docs/HOME-2026-10.md,
	// F4). Only a tools tab or the Study Hall tab may ask (matched by the sending window), and only
	// for a known soundscape.
	if (d.type === "revault-focus") {
		const id = tabOf(e.source);
		const t = id != null && state.tabs.find((x) => x.id === id);
		if (!t || typeof t.internal !== "string" || !(t.internal.startsWith("/tools") || t.internal === "/hall")) return;
		emitIsland("focus", { phase: d.phase, end: Number(d.end) || 0, tab: id });
		setFocusing("tab" + id, d.phase, d.end, () => frames.has(id));
		if (d.phase === "stop") focusStop();
		else if (MUSIC_PRESET_MAP[d.preset] && (d.phase === "work" || d.phase === "break")) {
			focusPlay(d.preset);
			if (d.phase === "break") focusDim();
			focusTab = id;
			if (!focusWatch) focusWatch = setInterval(() => { if (!focusOn() || !frames.has(focusTab)) { focusStop(); clearInterval(focusWatch); focusWatch = 0; } }, 2000); // the timer tab closed
		} else if (focusTab === id) focusStop(); // still focusing, with the sound switched off
		return;
	}
	if (d.type !== "revault-open" || typeof d.url !== "string") return;
	if (privacyUrl(d.url)) { openInternal(d.url, "Privacy"); return; } // the trust strip links to a paragraph
	if (/^\/supporter#(?:redeem|link)$/.test(d.url)) { openInternal(d.url, "Supporter"); return; } // Settings > Supporter shortcuts
	if (d.url === "/settings#set=carryCode") { openSettings("#set=carryCode"); return; } // the flashcards tool's carry code links
	// A tool pinned in a Study Hall room ("/tools/flashcards").
	const tool = /^\/tools\/([a-z]{2,20})$/.exec(d.url);
	if (tool) { const tt = TOOLS.find((x) => x.id === tool[1]); if (tt) openInternal(d.url, tt.name); return; }
	if (!OPENABLE.has(d.url)) return;
	if (d.url === "/") newTab();
	else if (d.url === "/linux") openLinux(false); // a link, not the tile: focus/open without booting
	else openInternal(d.url, PAGE_TITLES[d.url] || "Page");
});

// --- the status button (app/status.js) -------------------------------------------------------
const updateDot = initStatus({ status, connectionTitle, active, connState, announce, openInternal, openSettings, reloadWithEngine, changed: () => emitIsland("status", {}) });
window.addEventListener("online", () => { if (status.ready) reconnect().then(updateDot); else updateDot(); });

// --- live settings sync (app <-> settings tab) ---------------------------
let lastEngine = get("engine");
let lastTransport = get("transport");
subscribe((s) => {
	applyTheme();
	applyCloak();
	applyCollapse();
	if (status.ready) {
		if (s.engine !== lastEngine) { lastEngine = s.engine; setEngine(s.engine).then(updateDot, updateDot); }
		if (s.transport !== lastTransport) { lastTransport = s.transport; Promise.resolve(setTransport(s.transport)).then(updateDot, updateDot); }
	} else { lastEngine = s.engine; lastTransport = s.transport; }
	render();
	applyObfuscation(maxObf());
	// re-apply the address display so an obfuscation change takes effect now
	const a = active();
	if (a && document.activeElement !== addrInput) setAddr(a.addr);
});

// Warn before leaving with several active tabs (if enabled). An intentional
// auto-update reload (update.js sets __rvUpdating) must bypass this prompt.
window.addEventListener("beforeunload", (e) => {
	// A running Linux machine loses everything since its last save, so always ask (even when
	// "confirm closing many tabs" is off). The auto-updater never reloads while one runs (update.js).
	// Settings holding a carry code's last open asks too.
	if (linuxBusy() || anyUnsaved()) { e.preventDefault(); e.returnValue = ""; return; }
	if (window.__rvUpdating || !get("confirmCloseMany")) return;
	const real = state.tabs.filter((t) => t.url || t.internal || t.view).length;
	if (real >= 2) { e.preventDefault(); e.returnValue = ""; }
});

// --- go -------------------------------------------------------------------
restoreSession(); // reopen tabs after Re:Vault reloaded itself, or hold them as a "Restore N tabs" offer
initRestoreOffer();

// Deep link /?play=<book id>: opens that game in a tab. Copies of library pages running on other
// sites send their visitors here (see tools/books-guard.mjs). The parameter is removed at once so a
// reload or a shared URL doesn't reopen it.
const playLink = (function playDeepLink() {
	let id = null;
	try { id = new URLSearchParams(location.search).get("play"); } catch (e) {}
	if (id == null) return false;
	if (!/^\d{1,6}$/.test(id)) return true; // still came for a book: no welcome on this visit
	try { history.replaceState(null, "", location.pathname + location.hash); } catch (e) {}
	fetch("/books/books.json")
		.then((r) => r.json())
		.then((list) => {
			const g = Array.isArray(list) && list.find((b) => String(b.id) === id);
			if (!g) return;
			// No user gesture here, so a popup would be blocked: an isolate title in a shell that is
			// not isolated replaces this page with the top-level title instead.
			// Safari cannot isolate it top-level either, so it stays in a tab here (openBook says why).
			if (g.isolate === true && g.url && g.url.charAt(0) === "/" && self.crossOriginIsolated !== true && canIsolate()) { playPass().then(() => location.replace(g.url)); return; }
			openBook(g);
		})
		.catch(() => {});
	return true;
})();
// A game played for hours keeps its library pass.
keepPlayPass(() => state.tabs.some((t) => isGamePath(t.internal)));
// A Study Hall invite (/?s=invite#invite=CODE) opens Study Hall in a tab; a shared /?s=share link just loses its tag.
const inviteLink = takeArrival(() => openInternal("/hall", "Study Hall"));
render();
// Island Mode (docs/ISLAND-MODE.md): a lazy chunk, loaded only while the layout is "island". It shares
// this page's tabs, settings and players, so everything it touches is handed over here.
initIsland({
	state, active, render, frames, onRender, favOk, dispTitle, startEl, inOS, isPerf, announce, toast,
	get, set, setMany, subscribe,
	newTab, newTabAction, selectTab, closeTab, pinTab, closeOthers, closeRight, moveTab, reopenClosed, duplicateActive, openHome,
	navigate, navigateTab, back, forward, reload, retry, reloadWithEngine,
	openInternal, openSettings, openFolder, openLinux, openDesktop, openSignIn, getSession, onAccountChange, getStanding,
	commands, loadSettingsIndex, toggleFavorite, favorites, zoomActive, openFind,
	builtIns, openShortcut, openAll, icons: ICONS, tools: TOOLS, jumpBack: continueLinks,
	music: musicCtl, focusPlay, focusDim, focusStop, bellNext, activity, restoreOffer,
	store: { readJSON, writeJSON },
	presence: { setFocusing, lastStudying, lastGarden, paintGarden, onOnline },
	conn: () => { const cs = connState(); return navigator.onLine === false || cs === "offline" ? "offline" : cs === "wait" && status.ready ? "wait" : !status.ready ? "connecting" : status.healthy ? "ok" : "down"; },
	status: { state: statusState, name: statusName, glyph: statusGlyph, open: openStatus },
	extBar: extBarApi,
	openShare: () => openShareSheet(), openWhatsNew,
});
// Where the engine host reports proxied tabs when they run on the sandbox origin (app/sbx.js; unused
// in legacy mode). A link it hands over opens as a new proxied tab, checked like a Study Hall link.
const booting = boot({ nav: remoteNav, icon: remoteIcon, error: remoteError, fail: remoteFail, open: (u) => { const url = openableUrl(u); if (url) navigateTab(newTab(), url); } }).then(() => { updateDot(); loadRecovery().catch(() => {}); }).catch(() => {});

// A proxied page that was left in a browser tab of its own (its Re:Vault tab closed or reloaded)
// comes back here: /#open=<address> from that tab's handoff page (the fragment never reaches the
// server), or a message on "rv-open" when this Re:Vault tab is still open. Opens as a new tab,
// http(s) only, at most 3 a minute.
//
// Anyone can link to /#open=, so a link that did not come from our own handoff page (which leaves
// the same address in sessionStorage under "rv-open" just before it redirects) never opens a site
// by itself while sites run on this origin (sandbox off): the address waits in the address bar for
// the student to press Enter. With the sandbox on, sites open on the separate address either way.
let handoffs = [];
function openHandedOff(raw, trusted) {
	const now = Date.now();
	handoffs = handoffs.filter((t) => now - t < 60000);
	if (handoffs.length >= 3) return false;
	return Promise.all([decodeOpen(raw), booting]).then(([u]) => {
		const url = openableUrl(u || "");
		if (!url) return false;
		if (!trusted && !remote()) {
			if (islandAddr(url)) return false; // Island Mode has no start search box
			const box = !startEl.hidden ? $("rv-startsearch-input") : $("rv-addr");
			if (box) { box.value = url; try { box.focus(); box.select(); } catch (e) {} }
			return false;
		}
		handoffs.push(Date.now());
		navigateTab(newTab(), url);
		countStranded();
		return true;
	}).catch(() => false);
}
const openLink = (function openDeepLink() {
	let v = null, hash = location.hash;
	// the inline head script may have taken it out of the address already
	try { if (typeof window.__rvOpenHash === "string") hash = window.__rvOpenHash; delete window.__rvOpenHash; } catch (e) {}
	try { if (hash.startsWith("#open=")) v = decodeURIComponent(hash.slice(6)); } catch (e) { v = null; }
	if (v == null) return false;
	if (location.hash.startsWith("#open=")) { try { history.replaceState(null, "", location.pathname + location.search); } catch (e) {} }
	let trusted = false;
	try { trusted = !!v && sessionStorage.getItem("rv-open") === v; sessionStorage.removeItem("rv-open"); } catch (e) { trusted = false; }
	if (v) openHandedOff(v, trusted);
	return true;
})();
try {
	const ch = new BroadcastChannel("rv-open");
	const me = Math.random().toString(36).slice(2, 10);
	ch.onmessage = (e) => {
		const d = e.data || {};
		if (typeof d.id !== "string" || d.id.length > 16) return;
		// A visible Re:Vault tab answers first; a background one a little later.
		if (d.type === "ask") setTimeout(() => { try { ch.postMessage({ type: "offer", id: d.id, tab: me }); } catch (er) {} }, document.visibilityState === "visible" ? 0 : 150);
		else if (d.type === "open" && d.to === me && typeof d.url === "string") {
			Promise.resolve(openHandedOff(d.url, true)).then((ok) => { if (ok) { try { ch.postMessage({ type: "opened", id: d.id }); } catch (er) {} try { window.focus(); } catch (er) {} } });
		}
	};
} catch (e) { /* no BroadcastChannel: the handoff page opens /#open= itself */ }
initExtensions(); // relay network extensions (ad blocker / bandwidth saver) to the SW
initExtFrames(); // page-tool extensions (devtools / dark mode) injected into proxied tabs
initExtUI(); // toolbar extensions panel + incognito indicator
initExtBar(); // buttons for the extensions that are on
initHelperDock(); // Study Helper button + side panel (only when the server flag allows it)
initNavMore(); // phone toolbar: fold the extra buttons into a More tray
initPresence(); // anonymous online-count beacon for the /status page
// "About N people are studying right now" under the start page (shared/studying-now.js).
const studyingEl = $("rv-studying");
const gardenEl = $("rv-commons");
const paintStudy = () => { paintStudying(studyingEl, lastStudying(), get("studyingNow")); paintGarden(gardenEl, lastGarden(), get("commonsGarden")); };
onOnline(paintStudy);
subscribe(paintStudy);
// The worker counts this browser with the same anonymous id as the presence beacon.
try {
	const id = localStorage.getItem("revault:pid");
	if (id && navigator.serviceWorker) navigator.serviceWorker.ready.then((r) => { if (r.active) r.active.postMessage({ type: "RV_ID", id: id.replace(/[^a-zA-Z0-9_-]/g, "").slice(0, 40) }); }).catch(() => {});
} catch (e) {}
// First-run welcome (a no-op for returning visitors; never inside the desktop or on a /?play= link).
const openTab = (t) => !!(t.url || t.src || t.view || t.internal || t.restore);
const onboardingLive = initOnboarding(onboarding, {
	inOS,
	play: playLink || openLink || inviteLink,
	startEl,
	searchInput: $("rv-startsearch-input"),
	focusIsland, // Island Mode has no start page search box: Done goes to the island's
	get,
	setMany,
	presets: WALLPAPER_PRESET_MAP,
	fonts: { FONTS, loadFont, loadDisplayFont }, // for the look previews
	shortcuts: SHORTCUTS, // the start-screen registry, for step 2's note on what else stays
	openDesktop,
	// the start screen is showing, no tab has anything open in it, and "Restore N tabs" isn't waiting
	isStartVisible: () => !startEl.hidden && !state.tabs.some(openTab) && !restoreOffer.get(),
	// would leaving this page lose something (or ask "Leave site?")
	hasLiveTabs: () => linuxBusy() || anyUnsaved() || state.tabs.some(openTab),
	openLooks: () => openSettings("#look"),
});
// Study ledger (docs/HOME-2026-10.md, F1 + F5): the start page was shown (once per load),
// milestones are checked here only (tools write the ledger; toasts show once, in the app),
// and a growing to-do list feeds the "Plan ahead" quest without tools importing quests.
{
	let visited = false;
	const visit = () => { if (!visited && !startEl.hidden) { visited = true; markVisit(); } };
	visit();
	if (!visited) { const mo = new MutationObserver(() => { visit(); if (visited) mo.disconnect(); }); mo.observe(startEl, { attributes: true, attributeFilter: ["hidden"] }); }
	checkMilestones(true);
	// One toast across tabs: only a tab the student can see awards (a hidden tab catches up when
	// shown, by then usually finding it done), and a lock serializes two visible windows; the
	// award re-reads revault:quests inside it.
	const miles = () => {
		if (document.hidden) return;
		if (navigator.locks) navigator.locks.request("revault-milestones", () => { checkMilestones(); }).catch(() => {});
		else checkMilestones();
	};
	storeSubscribe(TOTALS_KEY, miles);
	storeSubscribe("revault:streak", miles); // a new best streak
	document.addEventListener("visibilitychange", miles);
	const todos = () => { const v = readJSON("revault:todo", []); return Array.isArray(v) ? v.length : 0; };
	let had = todos();
	storeSubscribe("revault:todo", () => { const n = todos(); if (n > had) track("todo-add", n - had); had = n; });
}
// The Vee bumper at the top of the start page (docs/BUMPERS.md): a lazy chunk, after load + idle.
initBumper(!playLink && !openLink && !inviteLink && welcomeDone, { startEl, el: $("rv-bumper"), isStartVisible: () => !startEl.hidden, get, setMany, subscribe, openSettings });
// The "This week" card + daily tip (docs/HOME-2026-10.md, F6/F7): a lazy chunk, loaded after load +
// idle on a returning visit with the start screen showing. Everything it needs is handed over here.
initHome(!onboardingLive && !playLink && !openLink && !inviteLink, {
	startEl,
	el: $("rv-home"),
	isStartVisible: () => !startEl.hidden,
	get, setMany, subscribe,
	store: { readJSON, writeJSON, subscribe: storeSubscribe },
	activity,
	quests: { questStats, onQuestChange },
	account: { getSession, onAccountChange, syncUp, syncDown, fetchBackup, refreshProfile, signIn },
	presence: { lastOnline, onOnline, lastHall, carryOn },
	health: loadHealth,
	emitIsland, islandOn,
	toast,
	inOS,
	openQuests: () => openFolder("quests"),
	openTool: (id, name, hash) => openInternal("/tools/" + id + (hash || ""), name),
	openHall: () => openInternal("/hall", "Study Hall"),
	openPrivacy: (url) => openInternal(privacyUrl(url) || "/privacy", "Privacy"),
	openSettings,
	openDesktop,
});
// Storage health (shared/storage-health.js, a lazy chunk): once a day after the start page, and when
// real study work appears, so Chromium can be asked to keep Re:Vault's saves. Never in the desktop.
if (!inOS) {
	const health = (fn) => loadHealth().then(fn).catch(() => {});
	const day = activity.dateKey();
	// Incognito or Keep history off: daily() keeps no note, so it would load the chunk after every
	// start just to do nothing. Settings already say so here, without loading anything.
	const noNotes = get("incognito") === true || get("keepHistory") === false;
	if (!noNotes && (readJSON("revault:health", null) || {}).day !== day) {
		const later = () => setTimeout(() => health((m) => m.daily()), 5000);
		if (document.readyState === "complete") later(); else addEventListener("load", later, { once: true });
	}
	if (navigator.storage && navigator.storage.persist) {
		let t = 0;
		const work = () => { clearTimeout(t); t = setTimeout(() => health((m) => m.maybePersist("work")), 3000); };
		for (const k of ["revault:flashcards", "revault:notes", activity.DAYS_KEY]) storeSubscribe(k, work);
	}
}
// After an update, the "What's new" note; on a third day, the once-a-week install note.
{
	const notes = { inOS, get, set, subscribe, emitIsland, islandOn, announce, toast, busy: () => { const a = active(); return !!(a && a.internal && /^\/(?:hall|tools\/timer)\b/.test(a.internal)); } };
	initWhatsNew(notes);
	initInstall(notes);
}
// Inside Re:Vault OS: the desktop hands us addresses; a blank start tab takes it, else a new tab does.
initOSHost((url) => { const t = active(); if (t && !t.url && !t.internal && !t.src && !t.view) navigate(url); else navigateTab(newTab(), url); });
window.__rvReady = true; // signals the boot-failure fallback in index.html that the app started

// Auto-update: detect a new deploy and converge onto it. Auto-reload only when
// safe - no live proxied tab is mid-use (or the tab is hidden) and nothing would
// be lost - otherwise update.js shows a dismissible toast. Runs after
// restoreSession() so tab state exists for the safety check.
initUpdates({
	isSafeToReload: () => {
		if (document.activeElement === addrInput) return false; // mid-typing a URL
		if (linuxBusy() || anyUnsaved()) return false; // a running Linux machine, or an unsaved carry copy, would be lost
		if (state.tabs.some((t) => t.loading)) return false; // a navigation is in flight
		const proxied = state.tabs.filter((t) => t.url || t.src);
		if (proxied.length === 0) return true; // only the start page / internal pages - nothing to lose
		if (get("sessionRestore") === false) return false; // a reload would drop the open tabs
		return document.hidden; // restorable, but don't yank an actively-viewed proxied page
	},
});

// Labeled ad slots (docs/ADS-PLUMBING.md): the lazy slots bundle loads on the first real
// interaction, only while Settings "Show ads" is on and the first-run welcome is closed. Server
// config decides the rest per host (house ads everywhere, network ads only on approved hosts).
initSlots({ get, subscribe, isPerf, framed: window.top !== window });
